import React, { useState, useCallback, useEffect } from "react";
import { useFocusEffect } from "@react-navigation/native";
import {
  View,
  StyleSheet,
  TouchableOpacity,
  Image,
  ActivityIndicator,
  Linking,
} from "react-native";
import { AppText } from "@/components/AppText";
import useUserStore from "@/store/user";
import { showError } from "@/utils/toast";
import Icons from "@/assets/icons";
import { FontFamilies } from "@/constants/Typography";
import usePaymentMethodCheck from "@/hooks/usePaymentMethodCheck";
import OfferDetailsPopup from "./OfferDetailsPopup";
import OfferSuccessModal from "@/components/OfferSuccessModal";
import { calculatePayoutAmount } from "@/utils/payout";
import useOfferStore from "@/store/offer";
import { TRACKING_EVENTS } from "@/constants/trackingEvents";
import { useTrackImpression, useTrackClick } from "@/hooks/tracking";
import FBTrackingService from "@/services/fbTrackingService";
import { Offer } from "@/types/offer";
import { isWeb } from "@/utils/platform";

type Props = {
  offer: Offer;
};

const OfferCard: React.FC<Props> = ({ offer }: Props) => {
  const { userProfile } = useUserStore();
  const [isLoading, setIsLoading] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const { checkPaymentMethod, isChecking } = usePaymentMethodCheck();
  const {
    shouldShowSuccessModal,
    pendingOffer,
    setPendingOffer,
    clearSuccessModal,
    clearPendingOffer,
  } = useOfferStore();

  // Track when card is shown
  useTrackImpression(TRACKING_EVENTS.ADGEM_OFFER_SHOWN, {
    offerId: offer.id,
    offerName: offer.name,
    amount: calculatePayoutAmount(offer.payout),
  });

  useEffect(() => {
    if (
      shouldShowSuccessModal &&
      pendingOffer &&
      pendingOffer.id === offer.id
    ) {
      setShowSuccessModal(true);
      clearSuccessModal();
    }
  }, [shouldShowSuccessModal, pendingOffer, offer.id, clearSuccessModal]);

  const handleFocus = useCallback(() => {
    if (
      pendingOffer &&
      pendingOffer.id === offer.id &&
      !shouldShowSuccessModal
    ) {
      const timer = setTimeout(() => {
        clearPendingOffer();
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [pendingOffer, offer.id, shouldShowSuccessModal, clearPendingOffer]);

  useFocusEffect(handleFocus);

  const handlePress = useTrackClick(TRACKING_EVENTS.ADGEM_OFFER_CLICKED, {
    offerId: offer.id,
    offerName: offer.name,
    amount: calculatePayoutAmount(offer.payout),
  })(() => {
    setShowPopup(true);
    // Track when details popup is viewed
    FBTrackingService.trackEvent(TRACKING_EVENTS.ADGEM_OFFER_DETAILS_VIEWED, {
      eventData: {
        offerId: offer.id,
        offerName: offer.name,
        amount: calculatePayoutAmount(offer.payout),
      },
    });
  });

  const handleContinue = useCallback(async () => {
    setShowPopup(false);
    if (!userProfile) return;

    const trackingData = {
      offerId: offer.id,
      offerName: offer.name,
      amount: calculatePayoutAmount(offer.payout),
    };

    // Track claim initiated
    FBTrackingService.trackEvent(TRACKING_EVENTS.ADGEM_OFFER_CLAIM_INITIATED, {
      eventData: trackingData,
    });

    try {
      setIsLoading(true);

      const clickUrl = offer.url.replace(
        "{playerid}",
        userProfile.id
      ).replace('&sid2=', `&sid2=${userProfile.id}`);

      // Set this offer as pending in store
      setPendingOffer(offer);

      // First check if user has a payment method registered
      const hasPaymentMethod = await checkPaymentMethod(
        clickUrl,
        calculatePayoutAmount(offer.payout)
      );

      if (!hasPaymentMethod) {
        // User will be automatically redirected to register payment method
        FBTrackingService.trackEvent(
          TRACKING_EVENTS.ADGEM_OFFER_PAYMENT_CHECK_REQUIRED,
          { eventData: trackingData }
        );
        setIsLoading(false);
        return;
      }

      // const preflightResult = await OfferServices.preflightCheck(clickUrl);

      // if (!preflightResult.success) {
      //   showError(preflightResult.message, "Offer Unavailable");
      //   setIsLoading(false);
      //   FBTrackingService.trackEvent(TRACKING_EVENTS.ADGEM_OFFER_CLAIM_FAILED, {
      //     eventData: {
      //       ...trackingData,
      //       reason: "Preflight check failed",
      //       message: preflightResult.message,
      //     },
      //   });
      //   return;
      // }
      const canOpen = await Linking.canOpenURL(clickUrl);

      if (!canOpen) {
        showError("Unable to open this offer. Please try again later.");
        FBTrackingService.trackEvent(TRACKING_EVENTS.ADGEM_OFFER_CLAIM_FAILED, {
          eventData: { ...trackingData, reason: "Cannot open URL" },
        });
        return;
      }
      if(isWeb) {
        window.open(clickUrl, "_blank");
      } else {
        await Linking.openURL(clickUrl);
      }
      setShowSuccessModal(true);

      // Track success
      FBTrackingService.trackEvent(TRACKING_EVENTS.ADGEM_OFFER_CLAIM_SUCCESS, {
        eventData: trackingData,
      });
    } catch (error) {
      setIsLoading(false);
      console.error("AdgemOfferCard error:", error);
      showError("Something went wrong. Please try again later.");
      FBTrackingService.trackEvent(TRACKING_EVENTS.ADGEM_OFFER_CLAIM_FAILED, {
        eventData: { ...trackingData, error: String(error) },
      });
    }
  }, [userProfile, checkPaymentMethod, setPendingOffer, offer]);

  const handleClosePopup = useCallback(() => {
    setShowPopup(false);
  }, []);

  const handleCloseSuccessModal = useCallback(() => {
    setShowSuccessModal(false);
  }, []);

  return (
    <View style={styles.card}>
      <View style={styles.content}>
        <View style={styles.topSection}>
          <View style={styles.iconContainer}>
            <View style={styles.whiteBackground} />
            {offer.image ? (
              <Image
                source={{ uri: offer.image }}
                style={styles.image}
                resizeMode="contain"
              />
            ) : (
              <View style={styles.placeholderIcon}>
                <AppText style={styles.placeholderText}>
                  {offer.name.charAt(0).toUpperCase()}
                </AppText>
              </View>
            )}
          </View>
          <View style={styles.textContainer}>
            <AppText style={styles.title}>
              Signup offer from {offer.name}
            </AppText>
            <AppText style={styles.amount}>
              ${calculatePayoutAmount(offer.payout)}
            </AppText>
          </View>
        </View>

        <View style={styles.divider} />

        <View style={styles.bottomSection}>
          <View style={styles.descriptionContainer}>
            <View style={styles.checkIconContainer}>
              <Icons.CheckCircle width={13} height={13} />
            </View>
            <AppText style={styles.description}>
              {offer.description}
            </AppText>
          </View>
          <TouchableOpacity
            style={[
              styles.button,
              (isLoading || isChecking) && styles.buttonDisabled,
            ]}
            onPress={handlePress}
            disabled={isLoading || isChecking}
            activeOpacity={0.8}
          >
            {isLoading || isChecking ? (
              <ActivityIndicator size="small" color="#FFFFFF" />
            ) : (
              <AppText style={styles.buttonText}>Claim</AppText>
            )}
          </TouchableOpacity>
        </View>
      </View>

      {/* Offer Details Popup */}
      <OfferDetailsPopup
        visible={showPopup}
        offer={offer}
        onClose={handleClosePopup}
        onContinue={handleContinue}
      />

      {/* Offer Success Modal */}
      <OfferSuccessModal
        visible={showSuccessModal}
        offer={offer}
        onClose={handleCloseSuccessModal}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  card: {
    backgroundColor: "#FFFFFF",
    borderRadius: 16,
    marginHorizontal: 16,
    marginVertical: 8,
    borderWidth: 1,
    borderColor: "#EBEBEB",
    overflow: "hidden",
  },
  content: {
    paddingVertical: 12,
    paddingHorizontal: 12,
    gap: 16,
  },
  topSection: {
    flexDirection: "row",
    alignItems: "center",
    gap: 16,
  },
  iconContainer: {
    width: 80,
    height: 80,
    borderRadius: 16,
    backgroundColor: "#F3F3F3",
    borderWidth: 1,
    borderColor: "#EBEBEB",
    overflow: "hidden",
    justifyContent: "center",
    alignItems: "center",
    position: "relative",
  },
  whiteBackground: {
    position: "absolute",
    width: 80,
    height: 80,
    backgroundColor: "#FFFFFF",
    borderRadius: 16,
  },
  image: {
    width: 80,
    height: 80,
    position: "absolute",
    alignSelf: "center",
  },
  placeholderIcon: {
    width: "100%",
    height: "100%",
    backgroundColor: "#F3F3F3",
    justifyContent: "center",
    alignItems: "center",
  },
  placeholderText: {
    fontFamily: FontFamilies.InterTight.SemiBold,
    fontSize: 24,
    color: "#6B7280",
  },
  textContainer: {
    flex: 1,
    gap: 4,
  },
  title: {
    fontFamily: FontFamilies.Inter.Regular,
    fontSize: 15,
    color: "#161D14",
  },
  amount: {
    fontFamily: FontFamilies.Inter.SemiBold,
    fontSize: 20,
    lineHeight: 24,
    color: "#161D14",
  },
  divider: {
    height: 1,
    backgroundColor: "#F3F3F3",
    marginHorizontal: 0,
  },
  bottomSection: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  descriptionContainer: {
    flex: 1,
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  checkIconContainer: {
    width: 13,
    height: 13,
    borderRadius: 6.5,
    backgroundColor: "#A3A3A3",
    justifyContent: "center",
    alignItems: "center",
  },
  description: {
    flex: 1,
    fontFamily: FontFamilies.Inter.Regular,
    fontSize: 13,
    lineHeight: 19,
    letterSpacing: -0.26,
    color: "#161D14",
  },
  button: {
    backgroundColor: "#0F9129",
    borderRadius: 100,
    paddingVertical: 8,
    alignItems: "center",
    justifyContent: "center",
    borderWidth: 1,
    borderColor: "rgba(255, 255, 255, 0.12)",
    height: 39,
    minWidth: 96,
    paddingHorizontal: 16,
  },
  buttonDisabled: {
    opacity: 0.5,
  },
  buttonText: {
    fontFamily: FontFamilies.InterTight.SemiBold,
    fontSize: 15,
    lineHeight: 23,
    color: "#FFFFFF",
  },
});

export default OfferCard;
